// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Styles, StylesProvider } from "@insite/content-library/additionalStyles";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HeaderSearchClickable from "@werner/App/Components/Common/HeaderSearchClickable";
import xHeaderUpdate from "@werner/App/Handlers/XNG/Header";
import useSize, { useElementSize } from "@werner/App/Hooks/useSize";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";

import {
    getAuthenticatedHeaderSignInWidget,
    getCartLinkWidget,
    getHeaderActionsWidget,
    getHeaderLinkListWidget,
    getHeaderSignInWidget,
    getLogoImageWidget,
    getMainNavigationWidget,
    getSecondaryNavigationWidget,
} from "@werner/App/Store/Selectors/WidgetSelectors";
import Close from "@werner/XDL/Icons/Close";
import HeaderMenu from "@werner/XDL/Icons/HeaderMenu";
import { color, layoutContainer, xClassNames } from "@werner/XDL/Utilities";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        // </XNG-Changes>
        headerAuthenitcatedSignInWidget: getAuthenticatedHeaderSignInWidget(state),
        isAuthenticated: xIsAuthenticated(state),
        // </XNG-Changes>
        logoImageWidget: getLogoImageWidget(state),
        mainNavigationWidget: getMainNavigationWidget(state),
        headerSignInWidget: getHeaderSignInWidget(state),
        cartLinkWidget: getCartLinkWidget(state),
        headerActionsWidget: getHeaderActionsWidget(state),
        secondaryNavigationWidget: getSecondaryNavigationWidget(state),
        headerLinkListWidget: getHeaderLinkListWidget(state),
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    xHeaderUpdate,
};

type Props = PageProps &
    HasShellContext &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CompactHeaderStyles {
    page?: MobiusPageProps;
    mainNavigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    pageInnerWrapper?: InjectableCss;
}

const boxShadowStyleMap: Dictionary<string> = {
    light: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
    medium: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    dark: "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
};

function generateShadow(props: ThemeProps<BaseTheme>) {
    const { isDropShadow, dropShadowStyle } = props.theme.header;
    if (isDropShadow && dropShadowStyle in boxShadowStyleMap) {
        return `box-shadow: ${boxShadowStyleMap[dropShadowStyle]};`;
    }
    return "";
}

function generateBorder(props: ThemeProps<BaseTheme>) {
    const { isBorder, borderColor, borderThickness, isBorderTop, isBorderLeft, isBorderBottom, isBorderRight } =
        props.theme.header;
    if (!isBorder) {
        return "border: none;";
    }

    let border = `border: ${borderThickness}px solid ${resolveColor(borderColor, props.theme)};`;

    if (!isBorderTop) {
        border += "border-top: none;";
    }
    if (!isBorderBottom) {
        border += "border-bottom: none;";
    }
    if (!isBorderLeft) {
        border += "border-left: none;";
    }
    if (!isBorderRight) {
        border += "border-right: none;";
    }

    return border;
}

export const compactHeaderStyles: CompactHeaderStyles = {
    page: {
        padding: 0, // * Padding doesn't work with fullWidth because of logic on Line 64 if Page component
        fullWidth: [true, true, true, true, true], // make the header full width and control the margins with container css
        css: css`
            position: fixed;
            top: 0;
            left: 0;
            z-index: ${getProp("theme.zIndex.compactHeader")};
            box-shadow: 0 0 8px 0 ${color("primary", 0.25)};
            opacity: 0;
            transform: translateY(-100%) translateX(0);
            padding: ${props => props.theme.header.padding};
            &&&& {
                position: fixed;
            }
            ${generateBorder}
            ${generateShadow}
        `,
    },
    logoWrapper: {
        css: css`
            display: flex;
            align-items: center;
            > *:first-child {
                margin-right: 10px;
            }
            > *:nth-child(2) {
                text-align: right;
            }
            & button[data-test-selector="expandMobileMenu"] {
                &:hover {
                    ${IconWrapper} {
                        color: ${color("text")};
                    }
                }
            }
            & .wrapper {
                width: 75px;
                height: 40px;
                margin-right: 25px;
            }
        `,
    },
    searchWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-grow: 1;
            background: ${color("common.ui5")};
            padding-top: 8px;
            padding-bottom: 8px;
            position: absolute;
            width: 100%;
            top: -100px;
            z-index: 1;
            transition: all 500ms ease-in-out;
            ${({ showMainNavigation, showSearchSectionOnMobile }: any) => {
                return css`
                    ${showMainNavigation && "top: 16px;"}
                    ${showSearchSectionOnMobile && "top: 100%;"}
                `;
            }}
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 30px;
        `,
    },
    mainNavigationWrapper: {
        css: css`
            position: absolute;
            background: ${color("primary")};
            width: 100%;
            top: -100px;
            transition: all 500ms ease-in-out;
            ${({ showMainNavigation, showSearchSectionOnMobile }: any) => {
                if (showMainNavigation) {
                    if (showSearchSectionOnMobile) {
                        return css`
                            top: calc(100% + 56px);
                        `;
                    }
                    return css`
                        top: 100%;
                    `;
                }
            }}

            > div {
                ${layoutContainer}
            }
        `,
    },
};

const styles = compactHeaderStyles;

export const createStylesProviderStyles = (theme: BaseTheme) =>
    ({
        mainNavigation: {
            menuItemTypography: {
                css: css`
                    font-weight: 500;
                    white-space: nowrap;
                `,
                color: theme.header.linkColor,
            },
        },
        logo: {
            img: {
                css: css`
                    width: 224px;
                    height: 50px;
                `,
            },
        },
        headerSearchInput: {
            searchInputStyles: {
                popoverContentBody: {
                    as: "div",
                    _height: "auto",
                    css: css`
                        display: inline-flex;
                        justify-content: flex-end;
                        border-radius: 3px;
                    `,
                },
                searchHistoryStyles: {
                    wrapper: {
                        css: css`
                            display: flex;
                            flex-direction: column;
                            width: 240px;
                            padding: 0 20px 15px 20px;
                        `,
                    },
                },
            },
        },
    }) as Styles;

const transitionStyles: Dictionary<{ opacity: number; transform: string }> = {
    entering: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    entered: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exiting: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exited: { opacity: 0, transform: "translateY(-100%) translateX(0)" },
};

const InnerWrapper = styled.div<{ isEdit?: boolean; isFullWidth?: boolean } & InjectableCss>`
    ${({ isFullWidth }) => {
        if (isFullWidth) {
            return css`
                position: relative;
                z-index: 10;
                background-color: ${color("common.white")};
                width: 100%;
            `;
        }

        return css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            width: 100%;
            justify-content: space-between;
            ${layoutContainer}
            padding-top: 16px;
            padding-bottom: 16px;
        `;
    }}

    ${injectCss}
`;

const CompactHeader = (props: Props) => {
    const { isCurrentPage, isEditing, isInShell } = props.shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const [isShow, setIsShow] = useState(false);
    const transitionElement = React.useRef<HTMLElement | null>(null);

    // <XNG-Changes>
    const [showMainNavigation, setShowMainNavigation] = useState(false);
    const [showSearchSectionOnMobile, setShowSearchSectionOnMobile] = useState(false);
    const headerWidth = typeof document !== "undefined" ? useElementSize(document.body)?.width : "100%";
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const stickyHeaderRef = useRef<HTMLDivElement>(null);
    const stickyHeaderHeight = useSize(stickyHeaderRef)?.height;
    // </XNG-Changes>

    // XNG-Changes: update stickyHeader height
    useEffect(() => {
        if (stickyHeaderHeight) {
            props.xHeaderUpdate({ key: "stickyHeader", value: stickyHeaderHeight });
        }
    }, [stickyHeaderHeight]);

    useEffect(() => {
        function eventHandler() {
            const scrollPos = Math.round(window.scrollY);
            // XNG-Changes
            // const windowWidth = Math.round(window.innerWidth);
            let isSticky = false;
            if (scrollPos > Number(props.theme.header.triggerPoint)) {
                setIsShow(true);
                isSticky = true;
            } else if (isShow && scrollPos < Number(props.theme.header.triggerPoint) + 150) {
                setIsShow(false);
                isSticky = false;
            }

            props.xHeaderUpdate({ key: "isSticky", value: isSticky });
        }
        window.addEventListener("scroll", eventHandler, { passive: true });

        return () => {
            window.removeEventListener("scroll", eventHandler);
        };
    }, [props.theme.header.triggerPoint, isShow]);

    useEffect(() => {
        setIsShow(Boolean(isShell));
    }, [isShell]);

    // <XNG-Changes>
    const openNavigationDrawer = () => {
        setShowMainNavigation(prevState => !prevState);
    };
    const toggleSearch = () => {
        setShowSearchSectionOnMobile(!showSearchSectionOnMobile);
    };

    if (isTabletScreen) {
        return null;
    }
    // </XNG-Changes>

    // XNG-Changes: structure
    return (
        <Transition nodeRef={transitionElement} in={isShow} timeout={0}>
            {state => (
                <Page
                    ref={transitionElement}
                    {...styles.page}
                    as="header"
                    background={props?.theme?.header?.backgroundColor}
                    // XNG-Changes
                    id="compact-header"
                    data-test-selector="compact-header"
                    className={xClassNames.append(isShow ? "shown-compact-header" : "hidden-compact-header")}
                    style={{
                        transition: "all 350ms ease",
                        pointerEvents: isShow ? "auto" : "none",
                        ...transitionStyles[state],
                        // XNG-Changes
                        maxWidth: headerWidth,
                    }}
                >
                    <StylesProvider styles={createStylesProviderStyles(props.theme)}>
                        <InnerWrapper
                            isEdit={isEdit}
                            isFullWidth={true}
                            {...styles.pageInnerWrapper}
                            ref={stickyHeaderRef}
                        >
                            <InnerWrapper isEdit={isEdit} isFullWidth={false} {...styles.pageInnerWrapper}>
                                <StyledWrapper {...styles.logoWrapper}>
                                    {props.isTabletScreen ? (
                                        createWidgetElement("Header/MainNavigation", {
                                            fields: { ...props.mainNavigationWidget },
                                        })
                                    ) : (
                                        <Clickable
                                            onClick={openNavigationDrawer}
                                            data-test-selector="expandMobileMenu"
                                            aria-label="main-navigation"
                                        >
                                            <Icon
                                                src={showMainNavigation ? Close : HeaderMenu}
                                                size={30}
                                                color="common.ui3"
                                            />
                                        </Clickable>
                                    )}
                                    {createWidgetElement("Basic/Logo", {
                                        fields: {
                                            mobileSpecificImage:
                                                props.logoImageWidget?.mobileSpecificImage ||
                                                props.logoImageWidget?.logoImage,
                                        },
                                    })}
                                    {createWidgetElement("Common/HeaderShipToAddress", {
                                        fields: {},
                                    })}
                                </StyledWrapper>
                                <StyledWrapper {...styles.actionsWrapper}>
                                    <HeaderSearchClickable isShow={showSearchSectionOnMobile} onClick={toggleSearch} />
                                    {/* XNG-Changes */}
                                    {props.isAuthenticated
                                        ? createWidgetElement("Common/HeaderSignIn", {
                                              fields: {
                                                  ...props.headerAuthenitcatedSignInWidget,
                                              },
                                              isCompactHeaderSingIn: true,
                                          } as any)
                                        : createWidgetElement("Common/HeaderSignIn", {
                                              fields: {
                                                  ...props.headerSignInWidget,
                                              },
                                              isCompactHeaderSingIn: true,
                                          } as any)}
                                    {/* XNG-Changes */}

                                    {createWidgetElement("Common/CartLink", {
                                        fields: {
                                            ...props.cartLinkWidget,
                                        },
                                        isCompactHeaderCart: true,
                                    } as any)}
                                </StyledWrapper>
                            </InnerWrapper>
                        </InnerWrapper>
                        <StyledWrapper
                            className="search-wrapper"
                            {...styles.searchWrapper}
                            {...{ showMainNavigation, showSearchSectionOnMobile }}
                        >
                            {createWidgetElement("CompactHeader/Search", { fields: { isCompactHeader: true } })}
                        </StyledWrapper>
                        <StyledWrapper
                            {...{ showMainNavigation, showSearchSectionOnMobile }}
                            {...styles.mainNavigationWrapper}
                        >
                            {createWidgetElement("Header/MainNavigation", {
                                fields: { ...props.mainNavigationWidget },
                            })}
                        </StyledWrapper>
                    </StylesProvider>
                </Page>
            )}
        </Transition>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withIsInShell(CompactHeader))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
